import axios, { AxiosError } from 'axios';
import { DocumentNode } from 'graphql';
import { GraphQLClient } from 'graphql-request';
import { parseAccessToken } from 'lib-shared-auth';

import { elevioActions } from 'components/conversation/components/integrations/elevio/src/elevio.actions';

import { ElevioAuthTokenResponse, fetchElevioAuthToken } from './elevio';
import { ElevioAccessTokenPayload } from './elevio.types';
import { elevioGraphqlRequestBaseQuery } from './elevioGraphqlRequestBaseQuery';
import { dispatch, getElevioState } from './elevioStore';

/**
 * A wrapper around graphqlRequestBaseQuery to allow headers to be set, as well
 * as handling errors or 404's when a company does not have an Elevio
 * workspace.
 */
export function elevioBaseQuery(client: GraphQLClient) {
  return async (
    args: { document: string | DocumentNode; variables?: any },
    api,
    extraOptions,
  ) => {
    // const existingLocalStorageToken =
    //   await elevioLocalStorageAuth.getElevioAuthToken();
    const existingReduxStoreToken = getElevioState().suggestions.token;
    const authState = getElevioState().suggestions.authState;

    if (!existingReduxStoreToken || authState === 'uninitialized') {
      dispatch(elevioActions.suggestions.fetchTokenPending());

      try {
        const result: ElevioAuthTokenResponse = await fetchElevioAuthToken();
        const parsedToken = parseAccessToken(
          result.elevioToken,
        ) as unknown as ElevioAccessTokenPayload;
        const companyUid = parsedToken.sub;
        const userId = parsedToken.userId;
        const userEmail = parsedToken.userName;
        dispatch(
          elevioActions.suggestions.fetchTokenSuccess({
            elevioAuthStatus: 'success',
            token: result.elevioToken,
            companyUid,
            userId,
            userEmail,
          }),
        );
      } catch (e: unknown) {
        dispatch(elevioActions.suggestions.fetchTokenError());

        const isError = axios.isAxiosError(e);
        if (isError) {
          const axiosError = e as AxiosError;
          return {
            error: {
              message: axiosError.message,
              status: axiosError.response?.status,
            },
          };
        }
        return {
          error: e,
        };
      }
    }

    // Set auth header
    const token: string = getElevioState().suggestions.token as string;
    const bearer = `Bearer ${token}`;
    client.setHeader('authorization', bearer);
    // Set company-uid header
    const companyUid: string = getElevioState().suggestions.companyUid;
    client.setHeader('company-uid', companyUid);

    try {
      const result = await elevioGraphqlRequestBaseQuery({ client })(
        args,
        api,
        extraOptions,
      );

      if (result.error?.code === 'UpgradeRequired') {
        dispatch(
          elevioActions.suggestions.subscriptionStatusError({
            errorMessage: result.error?.message,
          }),
        );
      } else {
        dispatch(elevioActions.suggestions.subscriptionStatusSuccess());
      }

      /**
       * Sometimes graphql errors will still be returned as a 200 response, so
       * we need to check for error messages here and return an error if one is
       * found.
       */
      if (result.error?.message) {
        return {
          error: {
            message: result.error?.message,
            code: result.error?.code,
          },
        };
      }
      return result;
    } catch (error) {
      return { error };
    }
  };
}
